import { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward, Close, SearchOutline } from '@lyearn/icons';
import { Box, Button, Input, Stack, Typography } from '@lyearn/ui';
import RichTextViewer from '@/components/RichTextViewer';
import OptionsList from '@/modules/corporate/modules/questions/fields/components/Player/OptionsList';
import ListItem from '@/modules/corporate/modules/questions/fields/components/Player/OptionsListItem';
import useMouseInside from '@/modules/user-survey/hooks/useMouseInside';
import useQuestionPlayerResponse from '@/modules/user-survey/hooks/useQuestionPlayerResponse';
import QuestionPlayerContainer from '@/modules/user-survey/questions/components/QuestionPlayerContainer';
import { QuestionPlayerProps } from '@/modules/user-survey/questions/types';
import { Option, ResourceResponseTypeEnum } from '@/types/schema';

const getKeyCode = (option: Option, index: number): any => {
  return 1 + index;
};
const adaptToQuestionAttempt = (response: QuestionPlayerProps['response']) => ({
  isSubmitted: false,
  submission: { optionResponse: response?.questionSurveyResponse?.optionResponse },
});

const MultiSelectQuestionPlayer = (props: QuestionPlayerProps) => {
  const {
    variant,
    question,
    userSurveyQuestionSettings,
    error,
    useRichTextForComment,
    response,
    onChange,
    commentTitle,
    disabled,
    requireComments,
  } = props;
  const actions = useQuestionPlayerResponse({ response, onChange });
  const [searchText, setSearchText] = useState<string>('');
  const [filteredOptions, setFilteredOptions] = useState(question.options || []);
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMouseInside = useMouseInside(containerRef);
  const showMultiselect = question.options?.length && question.options.length > 4;
  const debouncedFilter = useMemo(
    () =>
      debounce((text: string) => {
        const result = question.options?.filter((option) =>
          option.optionText.toLowerCase().includes(text.toLowerCase()),
        );
        setFilteredOptions(result || []);
      }, 300),
    [question.options],
  );

  const showClearFilter = !!searchText && !!question.options?.length && !filteredOptions.length;

  useEffect(() => {
    debouncedFilter(searchText);
    return () => {
      debouncedFilter.cancel();
    };
  }, [searchText, debouncedFilter]);
  const onSelectChange = useEvent((option: Option) => {
    if (disabled) {
      return;
    }

    let optionResponse = response?.questionSurveyResponse?.optionResponse ?? [];

    if (optionResponse.some((o) => o.optionId === option.optionId)) {
      optionResponse = optionResponse.filter((o) => o.optionId !== option.optionId);
    } else {
      optionResponse.push({ optionId: option.optionId, optionText: option.optionText });
    }

    actions.onQuestionResponseChange({
      responseType: ResourceResponseTypeEnum.MultiSelect,
      optionResponse,
    });
  });

  const multiSelectJsx = (
    <Stack
      ref={containerRef}
      className={variant === 'form' ? 'mt-16 relative' : 'mt-32 relative'}
      direction="col"
      gapY={24}>
      <Input
        placeholder={isMouseInside ? 'Search options' : 'Select options'}
        startAdornment={
          isMouseInside ? (
            <SearchOutline className="h-18 w-18" fillColorToken="icon-secondary" />
          ) : (
            <ArrowIosDownward className="h-18 w-18" fillColorToken="icon-secondary" />
          )
        }
        value={searchText}
        onChange={(event) => setSearchText(event.target.value)}
      />

      {isMouseInside ? (
        <Box className="w-full -mt-[1rem] z-999 rounded-8 p-8 obrder bg-white border border-border  max-h-[30rem] overflow-y-auto">
          <Stack direction="col" gapY={24}>
            <OptionsList
              ListItem={ListItem}
              getKeyCode={getKeyCode}
              options={(filteredOptions ?? []) as Option[]}
              questionAttempt={adaptToQuestionAttempt(response)}
              onChange={onSelectChange}
            />
          </Stack>
          {showClearFilter ? (
            <Stack
              alignItems="center"
              className="w-full py-24"
              direction="col"
              gapY={12}
              justifyContent="center">
              <Typography className="text-text-secondary" variant="body-short-02">
                {t('Common:EmptyState.NoResultsSearch', { searchText })}
              </Typography>
              <Button
                className="max-w-[15rem] border !border-border text-text-secondary"
                size="small"
                startIcon={<Close className="w-18 h-18" />}
                variant="outlined"
                onClick={() => setSearchText('')}>
                {t('Common:EmptyState.ClearFilters')}
              </Button>
            </Stack>
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );

  return (
    <QuestionPlayerContainer
      commentTitle={commentTitle}
      disabled={disabled}
      error={error}
      post={response?.post}
      question={question}
      requireComments={requireComments}
      useRichTextForComment={useRichTextForComment}
      userSurveyQuestionSettings={userSurveyQuestionSettings}
      variant={variant}
      onPostChange={actions.onPostChange}>
      {showMultiselect ? (
        multiSelectJsx
      ) : (
        <Stack className={variant === 'form' ? 'mt-16' : 'mt-32'} direction="col" gapY={24}>
          <OptionsList
            ListItem={ListItem}
            getKeyCode={getKeyCode}
            options={(filteredOptions ?? []) as Option[]}
            questionAttempt={adaptToQuestionAttempt(response)}
            onChange={onSelectChange}
          />
        </Stack>
      )}
      {response?.questionSurveyResponse?.optionResponse?.length && showMultiselect ? (
        <Stack direction="col">
          <Box className="mt-24 mb-12">
            <Typography variant="body-short-02"> {t('Common:SelectedOptions')} : </Typography>
          </Box>
          <Box className="flex gap-x-12 gap-y-12  flex-wrap">
            {response?.questionSurveyResponse?.optionResponse?.map((response, index) => {
              return (
                <RichTextViewer
                  key={index}
                  className="px-24 text-center  py-4 min-w-[10rem] rounded-8 border border-border"
                  html={response.optionText}
                />
              );
            })}
          </Box>
        </Stack>
      ) : null}
    </QuestionPlayerContainer>
  );
};

export default MultiSelectQuestionPlayer;
